import { Box } from '@mantine/core';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useLayoutStore } from '../../stores/layoutStore';
import { layout } from '../../theme/layout';
import { colors } from '../../theme/colors';
import { SidebarDrawer } from './SidebarDrawer';

interface ResponsiveLayoutProps {
  /** 左サイドバーコンテンツ */
  sidebar: React.ReactNode;
  /** 右パネルコンテンツ */
  rightPanel?: React.ReactNode;
  /** ヘッダー（サイドバー右端から画面右端まで） */
  header?: React.ReactNode;
  /** 中央エリアコンテンツ */
  children: React.ReactNode;
}

/**
 * レスポンシブ3カラムレイアウトコンポーネント
 *
 * ブレークポイントに応じて以下のレイアウトに切り替わる：
 * - xl (1408px以上): 3カラム - 左サイドバー266px固定、中央max 928px、右パネル272px、余白は右端
 * - lg (1200px以上): 3カラム - 左サイドバーmin 240px/max 260px、中央min 668px、右パネル272px
 * - md (992px以上): 2カラム - 中央flex、右パネル272px（サイドバーはDrawer）
 * - sm未満: 1カラム - 中央のみ100%幅（サイドバーはDrawer）
 *
 * ヘッダーはサイドバー右端から画面右端まで伸び、
 * 余分なスペースは右パネルの右側（画面右端）に配置される
 *
 * @example
 * ```tsx
 * <ResponsiveLayout
 *   sidebar={<LeftPanel {...props} />}
 *   rightPanel={<RightPanel {...props} />}
 *   header={<AppHeader {...props} />}
 * >
 *   <MainContent />
 * </ResponsiveLayout>
 * ```
 */
export function ResponsiveLayout({ sidebar, rightPanel, header, children }: ResponsiveLayoutProps) {
  const { isXl, isSm, showInlineSidebar, showRightPanel } = useResponsiveLayout();
  const { sidebarVisible } = useLayoutStore();

  // サイドバー幅の計算
  const getSidebarWidth = (): number | string => {
    if (!showInlineSidebar || !sidebarVisible) {
      return 0;
    }
    if (isXl) {
      // xl時: 固定幅266px
      return layout.sidebar.width;
    }
    // lg時: min 240px / max 260px（レスポンシブ幅）
    return layout.sidebar.maxWidth;
  };

  const sidebarWidth = getSidebarWidth();

  return (
    <Box
      style={{
        display: 'flex',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {/* SidebarDrawer: md以下で表示 */}
      {!showInlineSidebar && <SidebarDrawer>{sidebar}</SidebarDrawer>}

      {/* 左サイドバー: lg以上でインライン表示 */}
      {showInlineSidebar && (
        <Box
          style={{
            width: sidebarWidth,
            minWidth: sidebarVisible ? layout.sidebar.minWidth : 0,
            maxWidth: sidebarVisible ? (isXl ? layout.sidebar.width : layout.sidebar.maxWidth) : 0,
            flexShrink: 0,
            borderRight: sidebarVisible ? `1px solid ${layout.border.color}` : 'none',
            padding: sidebarVisible ? layout.spacing.md : 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            backgroundColor: colors.background.card,
          }}
        >
          {sidebarVisible && sidebar}
        </Box>
      )}

      {/* メインエリア（ヘッダー + コンテンツ） - サイドバー右端から画面右端まで */}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* ヘッダー: サイドバー右端から画面右端まで伸びる */}
        {header}

        {/* コンテンツエリア - xl時はセンターブロックを中央配置 */}
        <Box
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: showInlineSidebar ? 'center' : 'flex-start',
            minHeight: 0,
          }}
        >
          {/* センターブロック（中央+右パネル） - xl時は最大1200pxで中央配置 */}
          <Box
            style={{
              display: 'flex',
              maxWidth: showInlineSidebar
                ? layout.mainContent.maxWidth +
                  (showRightPanel && rightPanel ? layout.rightPanel.width : 0)
                : '100%',
              width: '100%',
            }}
          >
            {/* 中央エリア */}
            <Box
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: showInlineSidebar ? layout.mainContent.minWidth : 0,
                maxWidth: layout.mainContent.maxWidth,
                ...(!isSm
                  ? { padding: layout.responsive.paddingXs }
                  : {
                      paddingTop: layout.responsive.paddingTop,
                      paddingLeft: layout.responsive.paddingRight,
                      paddingRight: layout.responsive.paddingRight,
                    }),
              }}
            >
              {children}
            </Box>

            {/* 右パネル: md以上で表示 */}
            {showRightPanel && rightPanel && (
              <Box
                style={{
                  width: layout.rightPanel.width,
                  flexShrink: 0,
                  marginTop: layout.responsive.paddingTop,
                  padding: layout.spacing.md,
                  overflowY: 'auto',
                  backgroundColor: colors.gray[50],
                  borderLeft: `1px solid ${layout.border.colorLight}`,
                  borderTopLeftRadius: layout.border.radiusLg,
                  borderBottomLeftRadius: layout.border.radiusLg,
                }}
              >
                {rightPanel}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
